import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import AuthService from 'services/auth'
import Phone from 'services/Phone'
import EditCustomerModal from '../EditCustomerModal'
import ScheduleStatusModal from '../ScheduleStatusModal'
import SendCustomMessagesModal from '../SendCustomMessagesModal'
import CustomerModal from '../CustomerModal'
import RateMemberModal from '../RateMemberModal'
import EditScheduleForm from 'components/Forms/EditScheduleForm'
import SplitScheduleForm from 'components/Forms/SplitScheduleForm'
import RadioButton from 'components/RadioButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AlertDialog from 'components/AlertDialog'
import OptionDialog from 'components/OptionDialog'
import Logo from 'icons/logo-cutilei-simple.png'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaPlus, FaEdit, FaStar, FaTimesCircle, FaWhatsapp } from 'react-icons/fa'
import ReactTooltip from 'react-tooltip'
import { DateTime } from 'luxon'

import {
  Container,
  FormField,
  Row,
  Image,
  SubTitle,
  InfoText,
  WhiteText,
  Label,
  MemberPicture,
  ImageContainer,
  RatingContainer,
  CustomerButton,
  StatusGrid,
  StatusBlock,
  StatusButton,
  BottomRow,
  LoadingBackdrop,
  ErrorContainer
} from './styles'

export default function ScheduleDetailsModal ({
  visible,
  schedules = [],
  scheduleStatuses = [],
  workingDays = [],
  data: schedule,
  onEdit: handleEdit = () => {},
  onDelete: handleDelete = () => {},
  onDeleteAll: handleDeleteAll = () => {},
  onStatusListUpdated: handleStatusListUpdated = () => {},
  onEditStatus: handleEditStatus = () => {},
  onSplit: handleSplitSchedule = () => {},
  onClose: handleClose = () => {},
  onAddSchedule = () => {},
  onOpenBill = () => {},
  onEditBill = () => {}
}) {
  const scheduleDateTime = DateTime.fromFormat (`${schedule.date} ${schedule.time}`, 'yyyy-MM-dd H:mm')
  const isCompleted = DateTime.now () >= scheduleDateTime?.plus ({ minutes: schedule.max_delay + 1 })
  const customerSchedules = schedules.filter (s => (
    s.validated !== undefined && s.id !== schedule.id
    && (s.customer?.id === schedule.customer?.id && s.member?.id === schedule.member?.id) 
  ))
  const hasAgendaEditPermission = AuthService.hasPermission ('agenda', 'edit')
  const hasAgendaDeletePermission = AuthService.hasPermission ('agenda', 'delete')
  const hasCustomerEditPermission = AuthService.hasPermission ('customers', 'edit')
  const hasEditBillPermission = AuthService.hasPermission ('financial.open_bills', 'edit')
  const businessId = AuthService.getBusinessId ()
  const isAdmin = AuthService.isAdmin ()
  const token = AuthService.getToken ()

  const statusForAllSchedules = ['confirmed', 'waiting', 'in_progress', 'finished', 'missed']
  const [loadingBill, setLoadingBill] = useState (false)
  const [submitting, setSubmitting] = useState (false)
  const [deleting, setDeleting] = useState (false)
  const [validating, setValidating] = useState (false)
  const [customer, setCustomer] = useState (schedule.customer)
  const [errorMessage, setErrorMessage] = useState (null)
  const [showEditCustomerModal, setShowEditCustomerModal] = useState (false)
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] = useState (false)
  const [showSendCustomMessagesModal, setShowSendCustomMessagesModal] = useState (false)
  const [showStatusModal, setShowStatusModal] = useState (false)
  const [showEditStatusDialog, setShowEditStatusDialog] = useState (false)
  const [showEditAllStatusDialog, setShowEditAllStatusDialog] = useState (false)
  const [showDeleteDialog, setShowDeleteDialog] = useState (false)
  const [showInactivateDialog, setShowInactivateDialog] = useState (false)
  const [showValidationDialog, setShowValidationDialog] = useState (false)
  const [showRatingModal, setShowRatingModal] = useState (false)
  const [hasRating, setHasRating] = useState (!!schedule.memberRating)
  const [statusList, setStatusList] = useState (scheduleStatuses.map (status => ({
    ...status,
    disabled: status.codename === 'paid' ? true : schedule.canceled
  })))
  const [statusToEdit, setStatusToEdit] = useState (null)
  const [selectedStatus, setSelectedStatus] = useState ({
    codename: '', name: '', color: ''
  })
  const [status, setStatus] = useState (
    schedule.validated
      ? 'paid' 
      : schedule.cutilei_schedule && isCompleted
        ? 'missed' 
        : schedule.status
  )

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    setCustomer (schedule.customer)
    setErrorMessage (null)
  }, [schedule])

  useEffect (() => {
    setStatus (schedule.status)
  }, [schedule.status])

  useEffect (() => {
    handleStatusListUpdated (statusList)
  }, [statusList])

  // const businessStatuses = useMemo (() => (
  //   statusList.filter (status => status.business_id === businessId)
  // ), [statusList, businessId])

  const handleOpenBill = async () => {
    try {
      setLoadingBill (true)
      const customer = await getCustomer ()
      const status = 'waiting'
      const editedSchedules = []

      const { data: bill } = await cutileiApi.post ('/business_bills', {
        businessId,
        customerId: customer.id,
        scheduleId: schedule.id
      }, requestConfig)

      await Promise.all (
        [...customerSchedules, schedule].map (async schedule => {
          if (['scheduled', 'confirmed'].includes (schedule.status)) {
            await cutileiApi.post (
              `/schedules/${schedule.id}/status`, {status}, requestConfig
            )
            editedSchedules.push ({ ...schedule, status, bill_id: bill.id })
          }
        })
      )

      setLoadingBill (false)
      onOpenBill (bill)
      handleEditStatus (editedSchedules)
    } catch (error) {
      setLoadingBill (false)
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    }
  }

  const handleEditBill = async () => {
    try {
      setLoadingBill (true)

      const { data: bill } = await cutileiApi.get (
        `/business_bills/${schedule.bill_id}`, requestConfig
      )

      setLoadingBill (false)
      onEditBill (bill)
    } catch (error) {
      setLoadingBill (false)
      logApiError (error)
    }
  }

  const handleStatusChange = status => {
    setSelectedStatus (status)
    if (statusForAllSchedules.includes (status.codename) && customerSchedules.length > 0)
      toggleEditAllStatusDialog ()
    else toggleEditStatusDialog ()
  }

  const changeScheduleStatus = async (status, changeAllSchedules = false) => {
    const editedSchedules = []
    try {
      setSubmitting (true)

      await cutileiApi.post (`/schedules/${schedule.id}/status`, {status}, requestConfig)
      setStatus (status)
      editedSchedules.push ({ ...schedule, status })

      if (changeAllSchedules) await Promise.all (
        customerSchedules.map (async schedule => {
          await cutileiApi.post (`/schedules/${schedule.id}/status`, {status}, requestConfig)
          editedSchedules.push ({ ...schedule, status })
        })
      )

      handleEditStatus (editedSchedules)
    } catch (error) {
      logApiError (error)
      setErrorMessage (error.response?.data)
    } finally {
      setSubmitting (false)
    }
  }

  const cancelSchedule = async () => {
    try {
      setSubmitting (true)
      await cutileiApi.delete (`/schedules/${schedule.id}`, requestConfig)
      handleDelete (schedule)
      handleClose ()
    } catch (error) {
      logApiError (error)
      setErrorMessage (error.response?.data)
    } finally {
      setSubmitting (false)
    }
  }

  const cancelAllSchedules = async () => {
    const canceledSchedules = []
    try {
      setSubmitting (true)

      await Promise.all (
        [...customerSchedules, schedule].map (async schedule => {
          await cutileiApi.delete (`/schedules/${schedule.id}`, requestConfig)
          canceledSchedules.push (schedule)
        })
      )
      handleDeleteAll (canceledSchedules)
      handleClose ()
    } catch (error) {
      logApiError (error)
      setErrorMessage (error.response?.data)
    } finally {
      setSubmitting (false)
    }
  }

  const inactivateSchedule = async () => {
    try {
      setDeleting (true)
      await cutileiApi.put (`/schedules/${schedule.id}/activation`, {
        active: false
      }, requestConfig)

      handleDelete (schedule)
      handleClose ()
    } catch (error) {
      logApiError (error)
      setErrorMessage (error.response?.data)
    } finally {
      setDeleting (false)
    }
  }

  const validateSchedule = async () => {
    try {
      setValidating (true)
      await cutileiApi.post (`/schedules/${schedule.id}/validate`, {}, requestConfig)

      handleClose ()
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    } finally {
      setValidating (false)
    }
  }

  const undoScheduleValidation = async () => {
    try {
      setValidating (true)
      await cutileiApi.delete (`/schedules/${schedule.id}/validate`, requestConfig)

      handleClose ()
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    } finally {
      setValidating (false)
    }
  }

  const handleAddSchedule = async () => {
    const customer = await getCustomer ()
    onAddSchedule (customer)
  }

  const getCustomer = async () => {
    let customer = schedule.customer

    if (schedule.member && !customer) {
      const { data: customerData } = await cutileiApi.post (`/businesses/${businessId}/customers`, {
        name: schedule.member.name,
        email: schedule.member.email,
        phone: schedule.member.phone,
        gender: schedule.member.gender,
        birth: schedule.member.birth
      }, requestConfig)

      customer = customerData
    }

    return customer
  }

  const toggleEditCustomerModal = () => setShowEditCustomerModal (!showEditCustomerModal)
  const toggleCustomerDetailsModal = () => setShowCustomerDetailsModal (!showCustomerDetailsModal)
  const toggleSendCustomMessagesModal = () => setShowSendCustomMessagesModal (!showSendCustomMessagesModal)
  const toggleStatusModal = () => setShowStatusModal (!showStatusModal)
  const toggleEditStatusDialog = () => setShowEditStatusDialog (!showEditStatusDialog)
  const toggleEditAllStatusDialog = () => setShowEditAllStatusDialog (!showEditAllStatusDialog)
  const toggleDeleteDialog = () => setShowDeleteDialog (!showDeleteDialog)
  const toggleInactivateDialog = () => setShowInactivateDialog (!showInactivateDialog)
  const toggleValidationDialog = () => setShowValidationDialog (!showValidationDialog)
  const toggleRatingModal = () => setShowRatingModal (!showRatingModal)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Detalhes do agendamento</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Container>
            <Row style={{marginBottom: 10}}>
              {customer ? (
                <FormField>
                  <Label>Cliente</Label>
                  <CustomerButton onClick={toggleCustomerDetailsModal}>
                    <InfoText>
                      {customer.name + (customer.phone ? ` - ${Phone.formatPhone (customer.phone)}`: '')}
                    </InfoText>
                  </CustomerButton>
                </FormField>
              ) : (
                <FormField style={{alignItems: 'center'}}>
                  <ImageContainer>
                    <MemberPicture src={schedule.member?.profile_picture}/>
                    {schedule.member?.average_rating > 0 && (
                      <RatingContainer>
                        <FaStar size={12} color='#FFC100'/>
                        <SubTitle>
                          {schedule.member?.average_rating}
                        </SubTitle>
                      </RatingContainer>
                    )}
                  </ImageContainer>
                  <Row style={{justifyContent: 'center'}}>
                    <Image src={Logo} alt='Agendamento Cutilei'/>
                    <SubTitle>{schedule.member?.name}</SubTitle>
                  </Row>
                </FormField>
              )}
            </Row>
            <Row style={{justifyContent: 'center', gap: 10}}>
              {customer && hasCustomerEditPermission && (
                <SecondaryButton
                  label='Alterar dados'
                  onClick={toggleEditCustomerModal}
                  color='#606060'
                  style={{width: 160}}
                />
              )}
              <SecondaryButton
                label='Falar com cliente'
                leftIcon={<FaWhatsapp size={18} color='#FFFFFF'/>}
                onClick={toggleSendCustomMessagesModal}
                color='#44CF6C'
                style={{width: 160}}
              />
            </Row>
            {isAdmin && (
              <InfoText>ID: {schedule.id}</InfoText>
            )}
            <EditScheduleForm
              data={schedule}
              workingDays={workingDays}
              onEdit={handleEdit}
            />
            {hasAgendaEditPermission && (
              <BottomRow style={{marginBottom: 10}}>
                <SecondaryButton
                  label='Adicionar agendamento'
                  leftIcon={<FaPlus size={12} color='#FFC100'/>}
                  onClick={handleAddSchedule}
                  color='#606060'
                  style={{width: 210}}
                />
              </BottomRow>
            )}
            <SplitScheduleForm
              data={schedule}
              onSplit={handleSplitSchedule}
            />
            <Row style={{margin: 0}}>
              {schedule.cutilei_schedule && schedule.canceled && (
                <Row style={{gap: 7, alignItems: 'center', marginLeft: 5}}>
                  <FaTimesCircle color='#FF3939' size={17}/>
                  <SubTitle style={{color: '#FF3939'}}>
                    Agendamento cancelado
                  </SubTitle>
                </Row>
              )}
            </Row>
            {!schedule.cutilei_schedule && (
              <>
                <SubTitle>Status</SubTitle>
                <StatusGrid>
                  {submitting && (
                    <LoadingBackdrop>
                      <Loading/>
                    </LoadingBackdrop>
                  )}
                  {statusList.map (statusOption => (
                    <StatusBlock
                      key={statusOption.codename}
                      color={statusOption.color}
                      disabled={statusOption.disabled || !hasAgendaEditPermission}
                      onClick={statusOption.disabled || !hasAgendaEditPermission ? null : () => {
                        if (statusOption.codename === 'canceled') toggleDeleteDialog ()
                        else handleStatusChange (statusOption)
                      }}
                      data-for={statusOption.codename}
                      data-tip
                    >
                      <RadioButton
                        circleColor='#FFFFFF'
                        dotColor='#FFFFFF'
                        value={status === statusOption.codename}
                        disabled={statusOption.disabled || !hasAgendaEditPermission}
                        onValueChange={(_) => {
                          if (statusOption.codename === 'canceled') toggleDeleteDialog ()
                          else handleStatusChange (statusOption)
                        }}
                      />
                      <InfoText>{statusOption.name}</InfoText>
                      {statusOption.business_id && (
                        <StatusButton onClick={e => {
                          e.stopPropagation ()
                          setStatusToEdit (statusOption)
                          toggleStatusModal ()
                        }}>
                          <FaEdit color='#FFFFFF'/>
                        </StatusButton>
                      )}
                    </StatusBlock>
                  ))}
                  <ReactTooltip id='paid' effect='solid' place='bottom' backgroundColor='#252525' tooltipRadius='10'>
                    <Label style={{color: '#FFFFFF'}}>
                      Para salvar o agendamento como pago, abra e finalize a comanda dele
                    </Label>
                  </ReactTooltip>
                </StatusGrid>
              </>
            )}
            {/* {hasAgendaEditPermission && businessStatuses.length < 4 && (
              <AltButton style={{alignSelf: 'center'}} onClick={toggleStatusModal}>
                <FaPlus size={12}/>
                <WhiteText>Status personalizado</WhiteText>
              </AltButton>
            )} */}
            {errorMessage && (
              <ErrorContainer>
                <WhiteText>{errorMessage}</WhiteText>
              </ErrorContainer>
            )}
            <BottomRow style={{marginTop: 15}}>
              {hasEditBillPermission && (
                <SecondaryButton
                  label={schedule.bill_id ? 'Exibir comanda' : 'Abrir comanda'}
                  onClick={schedule.bill_id ? handleEditBill : handleOpenBill}
                  loading={loadingBill}
                  style={{width: 160}}
                />
              )}
              {schedule.cutilei_schedule && isCompleted && schedule.active && (
                schedule.validated ? (
                  !hasRating && hasAgendaEditPermission && (
                    <SecondaryButton
                      label='Avaliar membro'
                      onClick={toggleRatingModal}
                      leftIcon={<FaStar size={14} color='#FFC100'/>}
                      width={160}
                      style={{alignSelf: 'center'}}
                    />
                  )
                ) : (
                  hasAgendaDeletePermission && (
                    <SecondaryButton
                      label='Remover da agenda'
                      onClick={toggleInactivateDialog}
                      disabled={deleting}
                      loading={deleting}
                      color='#FF3939'
                      width={160}
                    />
                  )
                )
              )}
              {customer && (
                <>
                  <EditCustomerModal
                    visible={showEditCustomerModal}
                    data={customer}
                    onConfirm={setCustomer}
                    onClose={toggleEditCustomerModal}
                  />
                  <CustomerModal
                    visible={showCustomerDetailsModal}
                    data={customer}
                    onClose={toggleCustomerDetailsModal}
                  />
                </>
              )}
            </BottomRow>
            {schedule.cutilei_schedule && isAdmin && (
              <BottomRow style={{marginTop: 10}}>
                <SecondaryButton
                  label={schedule.validated ? 'Reverter validação' : 'Validar agendamento'}
                  onClick={toggleValidationDialog}
                  disabled={validating}
                  loading={validating}
                  color={schedule.validated ? '#FF3939' : '#44CF6C'}
                  width={160}
                />
                <SecondaryButton
                  label='Cancelar'
                  onClick={toggleDeleteDialog}
                  disabled={deleting}
                  loading={deleting}
                  color='#FF3939'
                  width={160}
                />
              </BottomRow>
            )}
          </Container>
          <ScheduleStatusModal
            visible={showStatusModal}
            data={statusToEdit}
            selectedStatus={status}
            onCreate={status => setStatusList (prevStatuses => ([...prevStatuses, status]))}
            onEdit={status => setStatusList (prevStatuses => (
              prevStatuses.map (s => s.codename === status.codename ? status : s)
            ))}
            onDelete={status => setStatusList (prevStatuses => (
              prevStatuses.filter (s => s.id !== status.id)
            ))}
            onClose={() => {
              setStatusToEdit (null)
              setShowStatusModal (false)
            }}
          />
          <SendCustomMessagesModal
            visible={showSendCustomMessagesModal}
            schedule={schedule}
            customer={customer || schedule.member}
            onClose={toggleSendCustomMessagesModal}
          />
          <AlertDialog
            visible={showEditStatusDialog}
            title='Atenção!'
            message={<>
              {`Deseja realmente alterar o status este agendamento para `}
              <b style={{color: selectedStatus.color}}>{selectedStatus.name}?</b>
            </>}
            confirmText='Sim'
            cancelText='Não'
            onConfirm={() => changeScheduleStatus (selectedStatus.codename, false)}
            onClose={toggleEditStatusDialog}
          />
          <OptionDialog
            visible={showEditAllStatusDialog}
            title='Atenção!'
            message={
              <InfoText>
                {`Deseja alterar o status de todos os agendamentos deste cliente para `}
                <b style={{color: selectedStatus.color}}>{selectedStatus.name}</b>
                {` ou alterar apenas este agendamento?`}
              </InfoText>
            }
            options={[
              {
                label: 'Alterar apenas este',
                onClick: () => changeScheduleStatus (selectedStatus.codename, false),
                dangerous: false
              },
              {
                label: 'Alterar todos',
                onClick: () => changeScheduleStatus (selectedStatus.codename, true),
                dangerous: false
              }
            ]}
            onClose={toggleEditAllStatusDialog}
            containerStyle={{width: 430}}
            buttonStyle={{width: 160}}
          />
          <OptionDialog
            visible={showDeleteDialog}
            title='Atenção!'
            message={
              customerSchedules.length > 0
                ? 'Deseja cancelar todos os agendamentos deste cliente ou apenas este agendamento?'
                : 'Deseja realmente cancelar este agendamento?'
            }
            options={customerSchedules.length > 0 
              ? [
                {label: 'Cancelar apenas este', onClick: cancelSchedule, dangerous: false},
                {label: 'Cancelar todos', onClick: cancelAllSchedules, dangerous: true}
              ] : [
                {label: 'Sim', onClick: cancelSchedule, dangerous: true},
                {label: 'Não', onClick: toggleDeleteDialog, dangerous: false}
              ]
            }
            onClose={toggleDeleteDialog}
            containerStyle={customerSchedules.length > 0 ? {width: 430} : {}}
            buttonStyle={customerSchedules.length > 0 ? {width: 180} : {}}
          />
          <AlertDialog
            visible={showInactivateDialog}
            title='Atenção!'
            message='Deseja realmente remover este agendamento da agenda?'
            confirmText='Sim'
            cancelText='Não'
            onConfirm={inactivateSchedule}
            onClose={toggleInactivateDialog}
            dangerous
          />
          <AlertDialog
            visible={showValidationDialog}
            title='Atenção!'
            message={
              `Deseja realmente ${schedule.validated
                ? 'reverter a validação deste agendamento?'
                : 'validar este agendamento?'}`
            }
            confirmText='Sim'
            cancelText='Não'
            onConfirm={schedule.validated ? undoScheduleValidation : validateSchedule}
            onClose={toggleValidationDialog}
          />
          <RateMemberModal
            visible={showRatingModal}
            schedule={schedule}
            onConfirm={rating => setHasRating (!!rating)}
            onClose={toggleRatingModal}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
